import { CalendarX, CheckCircle, Clock, PencilSimple } from '@phosphor-icons/react';
import type { ScheduledPostDto } from '@reelplicate/api-client/model';
import {
  getListScheduledPostsQueryKey,
  getListUpcomingScheduledPostsQueryKey,
  useMarkPostPosted,
  useMoveScheduledPost,
  useUnschedulePost,
} from '@reelplicate/api-client/scheduled-posts';
import {
  Button,
  Field,
  Inline,
  Modal,
  ModalContent,
  SegmentedControl,
  SegmentedControlItem,
  Stack,
  TextInput,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { formatWhen } from '@/lib/format-when';

import type { AccountGroup } from './calendar-model';
import { postZone, zoneLabel } from './create-post/form';
import { fromZoneInput, toLocalInput, toZoneInput } from './local-input';
import { PlatformStack } from './platform-stack';
import type { Platform } from './platforms';
import { PostAccounts } from './post-accounts';
import { PostCaption } from './post-caption';
import { PostResults } from './post-results';
import { PostSharePanel } from './post-share-panel';

/**
 * A post's details from the Calendar: what goes out, where and when, with
 * Move and Unschedule, and where it stands on each account. Posts on tracked
 * accounts are found on the platform a few minutes after their time; the
 * rest are marked as posted by hand, per account or all at once.
 */
export function PostDetailsModal({
  workspaceId,
  post,
  platforms,
  groups = [],
  workspaceZone,
  qrShareEnabled = false,
  now,
  onOpenChange,
  onEditDraft,
}: {
  workspaceId: string;
  post: ScheduledPostDto | null;
  platforms: Platform[];
  /** The brand and personas, for a persona's time zone. */
  groups?: AccountGroup[];
  /** The workspace's time zone, used when the persona has none. */
  workspaceZone?: string;
  /** Offer "Post from phone" (the workspace has QR share on). */
  qrShareEnabled?: boolean;
  /** The current time (fixed in tests); defaults to when it opened. */
  now?: Date;
  onOpenChange: (open: boolean) => void;
  /** Opens a draft in Create post to finish it. */
  onEditDraft: (post: ScheduledPostDto) => void;
}) {
  return (
    <Modal open={post !== null} onOpenChange={onOpenChange}>
      {post && (
        <PostDetails
          key={post.id}
          workspaceId={workspaceId}
          post={post}
          platforms={platforms}
          groups={groups}
          workspaceZone={workspaceZone}
          qrShareEnabled={qrShareEnabled}
          now={now}
          onDone={() => onOpenChange(false)}
          onEditDraft={() => onEditDraft(post)}
        />
      )}
    </Modal>
  );
}

function PostDetails({
  workspaceId,
  post,
  platforms,
  groups,
  workspaceZone,
  qrShareEnabled,
  now: fixedNow,
  onDone,
  onEditDraft,
}: {
  workspaceId: string;
  post: ScheduledPostDto;
  platforms: Platform[];
  groups: AccountGroup[];
  workspaceZone?: string;
  qrShareEnabled: boolean;
  now?: Date;
  onDone: () => void;
  onEditDraft: () => void;
}) {
  const isDraft = post.status === 'DRAFT';
  const isPublished = post.status === 'PUBLISHED';
  // A post still to go out can move, and a failed one too: it's tried again.
  const isFailed = post.status === 'FAILED';
  const canMove = post.status === 'SCHEDULED' || isFailed;
  const [openedAt] = useState(() => new Date());
  const now = fixedNow ?? openedAt;
  const queryClient = useQueryClient();
  const move = useMoveScheduledPost();
  const unschedule = useUnschedulePost();
  const markPosted = useMarkPostPosted();
  // Set while marking it posted: when that started, the latest it can have gone out.
  const [markingAt, setMarkingAt] = useState<Date | null>(null);
  const marking = markingAt !== null;
  const [postedWhen, setPostedWhen] = useState('');
  const postedAt = postedWhen ? new Date(postedWhen) : null;
  const postedInFuture = postedAt !== null && markingAt !== null && postedAt > markingAt;
  const scheduledAt = new Date(post.scheduledAt);
  // "When" is on the viewer's clock, or on the persona's (else the workspace's).
  const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const theirs = postZone(
    post.accounts.map((a) => a.id),
    post.accounts,
    groups,
    workspaceZone ?? localZone,
  );
  const offerZone = theirs.timeZone !== localZone;
  const [onTheirClock, setOnTheirClock] = useState(false);
  const zone = onTheirClock ? theirs.timeZone : undefined;
  const [when, setWhen] = useState(toLocalInput(scheduledAt));
  const next = when ? fromZoneInput(when, zone) : null;
  const switchClock = (theirClock: boolean) => {
    setWhen(toZoneInput(next ?? scheduledAt, theirClock ? theirs.timeZone : undefined));
    setOnTheirClock(theirClock);
  };
  const changed = next !== null && next.getTime() !== scheduledAt.getTime();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: getListScheduledPostsQueryKey(workspaceId),
      }),
      queryClient.invalidateQueries({
        queryKey: getListUpcomingScheduledPostsQueryKey(workspaceId),
      }),
    ]);

  const onMove = async () => {
    if (!next) return;
    try {
      await move.mutateAsync({
        id: workspaceId,
        postId: post.id,
        data: { scheduledAt: next.toISOString() },
      });
    } catch {
      return; // Shown as a toast.
    }
    await refresh();
    toast.success(`Moved to ${formatWhen(next)}`);
    onDone();
  };

  const onMarkPosted = async () => {
    if (!postedAt || postedInFuture) return;
    try {
      await markPosted.mutateAsync({
        id: workspaceId,
        postId: post.id,
        data: { postedAt: postedAt.toISOString() },
      });
    } catch {
      return; // Shown as a toast.
    }
    await refresh();
    toast.success(`Marked as posted ${formatWhen(postedAt)}`);
    onDone();
  };

  const onUnschedule = async () => {
    try {
      await unschedule.mutateAsync({ id: workspaceId, postId: post.id });
    } catch {
      return;
    }
    await refresh();
    toast.success(isDraft ? 'Draft deleted' : 'Post unscheduled');
    onDone();
  };

  const video = post.media[0]?.kind === 'video' ? post.media[0] : null;
  return (
    <ModalContent
      size="md"
      title={isDraft ? `Draft · ${post.title}` : post.title}
      description={`${formatWhen(scheduledAt)} · ${post.accounts.map((a) => a.handle).join(', ')}`}
      footerStart={
        isPublished ? undefined : marking ? (
          <Button variant="ghost" onClick={() => setMarkingAt(null)}>
            Cancel
          </Button>
        ) : (
          <Button
            variant="ghost"
            leadingIcon={CalendarX}
            loading={unschedule.isPending}
            onClick={() => void onUnschedule()}
          >
            {isDraft ? 'Delete draft' : 'Unschedule'}
          </Button>
        )
      }
      footer={
        isPublished ? undefined : isDraft ? (
          <Button leadingIcon={PencilSimple} onClick={onEditDraft}>
            Finish draft
          </Button>
        ) : marking ? (
          <Button
            leadingIcon={CheckCircle}
            disabled={!postedAt || postedInFuture}
            loading={markPosted.isPending}
            onClick={() => void onMarkPosted()}
          >
            Mark as posted
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              leadingIcon={CheckCircle}
              onClick={() => {
                const now = new Date();
                setPostedWhen(toLocalInput(now));
                setMarkingAt(now);
              }}
            >
              Mark as posted
            </Button>
            {canMove && (
              <Button
                leadingIcon={Clock}
                disabled={!changed}
                loading={move.isPending}
                onClick={() => void onMove()}
              >
                Move
              </Button>
            )}
          </>
        )
      }
    >
      <Stack gap={4}>
        <Inline gap={4} alignY="top">
          {video ? (
            <video
              src={video.url}
              aria-label={`Video: ${post.title}`}
              controls
              playsInline
              preload="metadata"
              className="aspect-[9/16] w-40 shrink-0 rounded-md bg-black object-contain"
            />
          ) : (
            <Inline as="ol" gap={2} aria-label="Slides" className="overflow-x-auto pb-1">
              {post.media.map((slide, i) => (
                <li key={slide.url} className="shrink-0">
                  <img
                    src={slide.url}
                    alt={`Slide ${i + 1}`}
                    className="bg-bg-subtle h-40 rounded-sm object-cover"
                  />
                </li>
              ))}
            </Inline>
          )}
          <Stack gap={3} className="min-w-0">
            <PlatformStack platforms={platforms} />
            <PostCaption
              workspaceId={workspaceId}
              post={post}
              editable={canMove || isDraft}
              onSaved={refresh}
            />
            {post.flow && (
              <RouterLink
                to={`/flows/${post.flow.id}`}
                className="text-body-sm text-text-brand focus-visible:shadow-focus-ring rounded-xs outline-none hover:underline"
              >
                Made by {post.flow.name}
              </RouterLink>
            )}
          </Stack>
        </Inline>
        {!isDraft && !marking && (
          <PostAccounts
            workspaceId={workspaceId}
            post={post}
            now={now}
            onChanged={refresh}
          />
        )}
        {isDraft ? (
          <Well tone="info">
            A draft doesn’t go out. Finish it to pick the media, caption and time and
            schedule it.
          </Well>
        ) : isPublished ? (
          <PostResults workspaceId={workspaceId} post={post} />
        ) : marking ? (
          <Field
            label="Posted at"
            helper="When it went out on every account still to go, in your local time"
            error={postedInFuture ? 'Pick a time that has already passed' : undefined}
          >
            <TextInput
              type="datetime-local"
              value={postedWhen}
              max={markingAt ? toLocalInput(markingAt) : undefined}
              onChange={(event) => setPostedWhen(event.target.value)}
            />
          </Field>
        ) : (
          <>
            {canMove && (
              <Stack gap={2}>
                <Field
                  label="When"
                  helper={`${
                    zone
                      ? `On ${theirs.persona ? `${theirs.persona}’s` : 'the workspace’s'} clock, ${zoneLabel(zone, next ?? scheduledAt)}`
                      : 'In your local time'
                  }${isFailed ? '. It’s looked for again after this time.' : ''}`}
                >
                  <TextInput
                    type="datetime-local"
                    value={when}
                    onChange={(event) => setWhen(event.target.value)}
                  />
                </Field>
                {offerZone && (
                  <SegmentedControl
                    aria-label="Time zone"
                    value={onTheirClock ? 'theirs' : 'mine'}
                    onValueChange={(value) => switchClock(value === 'theirs')}
                  >
                    <SegmentedControlItem value="mine">Your time</SegmentedControlItem>
                    <SegmentedControlItem value="theirs">
                      {theirs.persona ? `${theirs.persona}’s time` : 'Workspace time'}
                    </SegmentedControlItem>
                  </SegmentedControl>
                )}
              </Stack>
            )}
            {qrShareEnabled && post.status === 'SCHEDULED' && (
              <PostSharePanel workspaceId={workspaceId} postId={post.id} />
            )}
            <PostResults workspaceId={workspaceId} post={post} />
            <Well tone="info">
              On tracked accounts, posts are found on the platform a few minutes after
              their time, with their link and numbers. Posted somewhere else, or it wasn’t
              found? Mark it as posted by hand, on one account or all of them.
            </Well>
          </>
        )}
      </Stack>
    </ModalContent>
  );
}
